import React, { useRef, useState, useEffect } from 'react';
import DuplicateCardCleaner from '../utils/DuplicateCardCleaner';

function ImportModal({ isOpen, onClose, importCards, onImportSuccess, currentCards, saveCardsToStorage }) {
  const [importMethod, setImportMethod] = useState('predefined');
  const [predefinedTemplates, setPredefinedTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateStats, setDuplicateStats] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch available predefined templates on mount
  useEffect(() => {
    if (isOpen) {
      fetchPredefinedTemplates();
      setDuplicateStats(null); // Reset duplicate stats when modal opens
    }
  }, [isOpen]);

  const fetchPredefinedTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Attempting to fetch templates from /templates/index.json');
      const response = await fetch(`${process.env.PUBLIC_URL}/templates/index.json`);
      
      console.log('Fetch response:', response);
      if (!response.ok) {
        throw new Error(`Failed to fetch templates index: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Templates data:', data);
      setPredefinedTemplates(data.templates || []);
    } catch (err) {
      console.error('Detailed error fetching templates:', err);
      setError(`Failed to load predefined templates: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to check for duplicates after import
  const checkAndRemoveDuplicates = () => {
    // Get latest cards from storage or use the currentCards prop
    const allCards = currentCards || [];
    
    if (allCards.length <= 1) return { hasDuplicates: false };
    
    // Use the DuplicateCardCleaner to find and remove duplicates
    const { removedDuplicates, cleanedCards, hasDuplicates } = 
      DuplicateCardCleaner.cleanupDuplicates(allCards, saveCardsToStorage);
    
    if (hasDuplicates) {
      // Show notification about removed duplicates
      window.toast?.info(`Removed ${removedDuplicates.length} duplicate card${removedDuplicates.length > 1 ? 's' : ''}`);
      
      // Set stats for possible display in the UI
      setDuplicateStats({
        removed: removedDuplicates.length,
        total: allCards.length,
        remaining: cleanedCards.length
      });
    }
    
    return { hasDuplicates, removedDuplicates, cleanedCards };
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        // Import cards and then trigger success callback
        const importedData = importCards(event.target.result);
        
        // Check for duplicates after import
        const { hasDuplicates } = checkAndRemoveDuplicates();
        
        // Notify parent component that import was successful
        if (onImportSuccess) {
          onImportSuccess(importedData, hasDuplicates);
        }
        
        onClose();
      } catch (err) {
        setError('Error processing imported file: ' + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleImportTemplate = async () => {
    if (!selectedTemplate) return;
    
    setLoading(true);
    setError(null);
    try {
      const templatePath = `${process.env.PUBLIC_URL}/templates/${selectedTemplate.fileName}`;
      console.log('Attempting to fetch template from:', templatePath);
      
      const response = await fetch(templatePath);
      console.log('Template fetch response:', response);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch template data: ${response.status} ${response.statusText}`);
      }
      
      const templateData = await response.json();
      console.log('Template data:', templateData);
      
      // Import cards and capture any returned data
      const importedData = importCards(JSON.stringify(templateData));
      
      // Check for duplicates after import
      const { hasDuplicates } = checkAndRemoveDuplicates();
      
      // Notify parent component that import was successful
      if (onImportSuccess) {
        onImportSuccess(importedData, hasDuplicates);
      }
      
      onClose();
    } catch (err) {
      console.error('Detailed error importing template:', err);
      setError(`File not found: 4O4`);
    } finally {
      setLoading(false);
    }
  };

  // Function to get appropriate emoji icon based on template name/description
  const getTemplateIcon = (template) => {
    const name = template.name.toLowerCase();
    
    if (name.includes('language') || name.includes('vocab')) return '🔤';
    if (name.includes('science')) return '🔬';
    if (name.includes('math')) return '🧮';
    if (name.includes('history')) return '📜';
    if (name.includes('geography')) return '🌎';
    if (name.includes('programming')) return '💻';
    if (name.includes('music')) return '🎵';
    if (name.includes('art')) return '🎨';
    
    // Default icon
    return '📚';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Import Cards</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-content">
          <div className="import-options">
            <div 
              className={`import-option ${importMethod === 'predefined' ? 'active' : ''}`}
              onClick={() => setImportMethod('predefined')}
            >
              <div className="option-icon">📋</div>
              <div className="option-label">Predefined Templates</div>
            </div>
            
            <div 
              className={`import-option ${importMethod === 'upload' ? 'active' : ''}`}
              onClick={() => setImportMethod('upload')}
            >
              <div className="option-icon">📁</div>
              <div className="option-label">Upload from Computer</div>
            </div>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          {duplicateStats && (
            <div className="duplicate-info">
              <span className="info-icon">ℹ️</span>
              <span>Removed {duplicateStats.removed} duplicate cards.</span>
            </div>
          )}
          
          {importMethod === 'predefined' ? (
            <div className="predefined-templates">
              <h3>Select a Template</h3>
              
              {loading ? (
                <div className="loading-indicator">Loading templates...</div>
              ) : (
                <>
                  {predefinedTemplates.length === 0 ? (
                    <div className="no-templates">No predefined templates available</div>
                  ) : (
                    <div className="template-grid">
                      {predefinedTemplates.map((template, index) => (
                        <div 
                          key={index}
                          className={`template-box ${selectedTemplate?.fileName === template.fileName ? 'selected' : ''}`}
                          onClick={() => handleSelectTemplate(template)}
                        >
                          <div className="template-icon">{getTemplateIcon(template)}</div>
                          <div className="template-name">{template.name}</div>
                          <div className="template-description">{template.description}</div>
                          <div className="template-count">{template.cardCount} cards</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="file-upload">
              <p>Select a JSON file from your computer to import cards.</p>
              <button 
                className="browse-button"
                onClick={() => fileInputRef.current.click()}
              >
                Browse Files
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                accept=".json" 
                onChange={handleFileChange} 
              />
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          {importMethod === 'predefined' && (
            <button 
              className="import-button" 
              onClick={handleImportTemplate}
              disabled={!selectedTemplate || loading}
            >
              {loading ? 'Importing...' : 'Import Selected Template'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportModal;